<template>
  <div class="bank-update-requests-container">
    <div class="container-fluid">
      <div class="row">
        <div class="col-sm-12">
          <div class="card">
            <div class="card-header pb-0">
              <div class="d-flex justify-content-between align-items-center">
                <h5>Bank Update Requests</h5>
                <div class="d-flex gap-2">
                  <span class="badge badge-warning" v-if="pendingCount > 0">
                    {{ pendingCount }} Pending
                  </span>
                  <button class="btn btn-sm btn-primary" @click="refreshData">
                    <i class="fa fa-refresh"></i> Refresh
                  </button>
                </div>
              </div>
            </div>

            <div class="card-body">
              <!-- Filters -->
              <div class="row mb-3">
                <div class="col-md-3">
                  <label>Status Filter</label>
                  <select v-model="filters.status" @change="applyFilters" class="form-control">
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label>Employee Name</label>
                  <input 
                    type="text" 
                    v-model="filters.employeeName" 
                    @input="onEmployeeNameChange"
                    class="form-control" 
                    placeholder="Search by employee name..."
                  />
                </div>
                <div class="col-md-3">
                  <label>Employee ID</label>
                  <input 
                    type="text" 
                    v-model="filters.employeeId" 
                    @input="onEmployeeIdChange"
                    class="form-control" 
                    placeholder="Enter employee ID..."
                  />
                </div>
                <div class="col-md-3">
                  <label>Site Name</label>
                  <input 
                    type="text" 
                    v-model="filters.siteName" 
                    @input="onSiteNameChange"
                    class="form-control" 
                    placeholder="Search by site name..."
                  />
                </div>
                <div class="col-md-3 mt-3">
                  <label>Items per page</label>
                  <select v-model="filters.limit" @change="applyFilters" class="form-control">
                    <option :value="10">10</option>
                    <option :value="20">20</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                  </select>
                </div>
                <div class="col-md-12 mt-2" v-if="hasActiveFilters">
                  <button class="btn btn-sm btn-outline-secondary" @click="clearFilters">
                    <i class="fa fa-times"></i> Clear All Filters
                  </button>
                </div>
              </div>

              <!-- Loading State -->
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2">Loading bank update requests...</p>
              </div>

              <!-- No Data State -->
              <div v-else-if="!requests || requests.length === 0" class="text-center py-5">
                <i class="fa fa-inbox fa-3x text-muted mb-3"></i>
                <p class="text-muted">No bank update requests found</p>
              </div>

              <!-- Data Table -->
              <div v-else class="table-responsive">
                <table class="table table-hover table-striped">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Employee Code</th>
                      <th>Site Name</th>
                      <th>Account Number</th>
                      <th>Bank Name</th>
                      <th>IFSC Code</th>
                      <th>Requested Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="request in requests" :key="request._id">
                      <td>
                        <div class="d-flex flex-column">
                          <span class="fw-bold">{{ request.employeeId?.employeeName || 'N/A' }}</span>
                          <small class="text-muted">{{ request.employeeId?.email || '' }}</small>
                        </div>
                      </td>
                      <td>{{ request.employeeId?.employeeCode || 'N/A' }}</td>
                      <td>
                        <div class="d-flex flex-column">
                          <span>{{ request.employeeId?.siteId?.siteName || 'N/A' }}</span>
                        </div>
                      </td>
                      <td>
                        <code>{{ request.accountNumber }}</code>
                      </td>
                      <td>{{ request.bankName }}</td>
                      <td>
                        <code>{{ request.ifscCode }}</code>
                      </td>
                      <td>
                        <small>{{ formatDate(request.requestedAt) }}</small>
                      </td>
                      <td>
                        <span :class="getStatusBadge(request.status).class">
                          {{ getStatusBadge(request.status).label }}
                        </span>
                      </td>
                      <td>
                        <div class="btn-group" role="group">
                          <button 
                            class="btn btn-sm btn-info" 
                            @click="viewDetails(request._id)"
                            title="View Details"
                          >
                            <i class="fa fa-eye"></i>
                          </button>
                          <button 
                            v-if="request.status === 'pending'"
                            class="btn btn-sm btn-success" 
                            @click="approveRequest(request)"
                            title="Approve"
                          >
                            <i class="fa fa-check"></i>
                          </button>
                          <button 
                            v-if="request.status === 'pending'"
                            class="btn btn-sm btn-danger" 
                            @click="showRejectModal(request)"
                            title="Reject"
                          >
                            <i class="fa fa-times"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination -->
              <div v-if="pagination && pagination.totalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
                <div>
                  Showing {{ ((pagination.page - 1) * pagination.limit) + 1 }} 
                  to {{ Math.min(pagination.page * pagination.limit, pagination.total) }} 
                  of {{ pagination.total }} entries
                </div>
                <nav>
                  <ul class="pagination mb-0">
                    <li class="page-item" :class="{ disabled: pagination.page === 1 }">
                      <a class="page-link" @click.prevent="changePage(pagination.page - 1)" href="#">
                        Previous
                      </a>
                    </li>
                    <li 
                      v-for="page in visiblePages" 
                      :key="page" 
                      class="page-item" 
                      :class="{ active: page === pagination.page }"
                    >
                      <a class="page-link" @click.prevent="changePage(page)" href="#">
                        {{ page }}
                      </a>
                    </li>
                    <li class="page-item" :class="{ disabled: pagination.page === pagination.totalPages }">
                      <a class="page-link" @click.prevent="changePage(pagination.page + 1)" href="#">
                        Next
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Reject Modal -->
    <div v-if="showRejectDialog" class="modal fade show" style="display: block; background: rgba(0,0,0,0.5);" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Reject Bank Update Request</h5>
            <button type="button" class="btn-close" @click="closeRejectModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Employee: <strong>{{ selectedRequest?.employeeId?.employeeName }}</strong></label>
            </div>
            <div class="mb-3">
              <label class="form-label">Rejection Reason <span class="text-danger">*</span></label>
              <textarea 
                v-model="rejectionReason" 
                class="form-control" 
                rows="4" 
                placeholder="Enter the reason for rejection..."
                required
              ></textarea>
              <small class="text-muted">Please provide a clear reason for the employee</small>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeRejectModal">Cancel</button>
            <button 
              type="button" 
              class="btn btn-danger" 
              @click="confirmReject"
              :disabled="!rejectionReason || rejectionReason.trim() === ''"
            >
              <i class="fa fa-times"></i> Reject Request
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import {
  getAllBankUpdateRequests,
  approveBankUpdateRequest,
  rejectBankUpdateRequest,
  getPendingBankUpdateRequestsCount,
  formatDate,
  getStatusBadge
} from '@/services/bankUpdateRequestService';

export default defineComponent({
  name: 'BankUpdateRequestsList',
  setup() {
    const router = useRouter();
    const requests = ref<any[]>([]);
    const loading = ref(false);
    const pendingCount = ref(0);
    const pagination = ref<any>(null);
    const filters = ref({
      status: '',
      employeeName: '',
      employeeId: '',
      siteName: '',
      page: 1,
      limit: 10
    });

    // Debounce timer for search inputs
    let searchTimeout: any = null;

    // Reject modal
    const showRejectDialog = ref(false);
    const selectedRequest = ref<any>(null);
    const rejectionReason = ref('');

    // Fetch data
    const fetchRequests = async () => {
      loading.value = true;
      try {
        const response = await getAllBankUpdateRequests(filters.value);
        if (response.data.success) {
          requests.value = response.data.data || [];
          pagination.value = response.data.pagination || null;
        } else {
          console.error('Failed to fetch requests:', response.data.message);
        }
      } catch (error) {
        console.error('Error fetching bank update requests:', error);
        alert('Failed to load bank update requests. Please try again.');
      } finally {
        loading.value = false;
      }
    };

    // Fetch pending count
    const fetchPendingCount = async () => {
      try {
        const response = await getPendingBankUpdateRequestsCount();
        if (response.data.success) {
          pendingCount.value = response.data.count || 0;
        }
      } catch (error) {
        console.error('Error fetching pending count:', error);
      }
    };

    // Apply filters
    const applyFilters = () => {
      filters.value.page = 1; // Reset to first page
      fetchRequests();
    };

    // Handle employee name change with debounce
    const onEmployeeNameChange = () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      searchTimeout = setTimeout(() => {
        applyFilters();
      }, 500); // Wait 500ms after user stops typing
    };

    // Handle employee ID change with debounce
    const onEmployeeIdChange = () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      searchTimeout = setTimeout(() => {
        applyFilters();
      }, 500); // Wait 500ms after user stops typing
    };

    // Handle site name change with debounce
    const onSiteNameChange = () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      searchTimeout = setTimeout(() => {
        applyFilters();
      }, 500); // Wait 500ms after user stops typing
    };

    // Clear all filters
    const clearFilters = () => {
      filters.value.status = '';
      filters.value.employeeName = '';
      filters.value.employeeId = '';
      filters.value.siteName = '';
      filters.value.page = 1;
      fetchRequests();
    };

    // Check if any filters are active
    const hasActiveFilters = computed(() => {
      return !!(filters.value.status || filters.value.employeeName || filters.value.employeeId || filters.value.siteName);
    });

    // Refresh data
    const refreshData = () => {
      fetchRequests();
      fetchPendingCount();
    };

    // View details
    const viewDetails = (requestId: string) => {
      router.push(`/bank-update-requests/details/${requestId}`);
    };

    // Approve request
    const approveRequest = async (request: any) => {
      if (!confirm(`Are you sure you want to approve the bank update request for ${request.employeeId?.employeeName}?`)) {
        return;
      }

      try {
        const adminId = localStorage.getItem('userId') || localStorage.getItem('adminId');
        if (!adminId) {
          alert('Admin ID not found. Please login again.');
          return;
        }

        const response = await approveBankUpdateRequest(request._id, adminId);
        if (response.data.success) {
          alert('Bank update request approved successfully!');
          refreshData();
        } else {
          alert(response.data.message || 'Failed to approve request');
        }
      } catch (error: any) {
        console.error('Error approving request:', error);
        alert(error.response?.data?.message || 'Failed to approve request');
      }
    };

    // Show reject modal
    const showRejectModal = (request: any) => {
      selectedRequest.value = request;
      rejectionReason.value = '';
      showRejectDialog.value = true;
    };

    // Close reject modal
    const closeRejectModal = () => {
      showRejectDialog.value = false;
      selectedRequest.value = null;
      rejectionReason.value = '';
    };

    // Confirm reject
    const confirmReject = async () => {
      if (!rejectionReason.value || rejectionReason.value.trim() === '') {
        alert('Please enter a rejection reason');
        return;
      }

      try {
        const adminId = localStorage.getItem('userId') || localStorage.getItem('adminId');
        if (!adminId) {
          alert('Admin ID not found. Please login again.');
          return;
        }

        const response = await rejectBankUpdateRequest(
          selectedRequest.value._id,
          adminId,
          rejectionReason.value
        );

        if (response.data.success) {
          alert('Bank update request rejected successfully!');
          closeRejectModal();
          refreshData();
        } else {
          alert(response.data.message || 'Failed to reject request');
        }
      } catch (error: any) {
        console.error('Error rejecting request:', error);
        alert(error.response?.data?.message || 'Failed to reject request');
      }
    };

    // Change page
    const changePage = (page: number) => {
      if (page < 1 || page > pagination.value.totalPages) return;
      filters.value.page = page;
      fetchRequests();
    };

    // Computed visible pages
    const visiblePages = computed(() => {
      if (!pagination.value) return [];
      const total = pagination.value.totalPages;
      const current = pagination.value.page;
      const pages = [];
      
      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    });

    // Initialize
    onMounted(() => {
      fetchRequests();
      fetchPendingCount();
    });

    return {
      requests,
      loading,
      pendingCount,
      pagination,
      filters,
      showRejectDialog,
      selectedRequest,
      rejectionReason,
      visiblePages,
      hasActiveFilters,
      fetchRequests,
      applyFilters,
      onEmployeeNameChange,
      onEmployeeIdChange,
      onSiteNameChange,
      clearFilters,
      refreshData,
      viewDetails,
      approveRequest,
      showRejectModal,
      closeRejectModal,
      confirmReject,
      changePage,
      formatDate,
      getStatusBadge
    };
  }
});
</script>

<style scoped>
.bank-update-requests-container {
  padding: 20px 0;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
  white-space: nowrap;
}

.badge {
  padding: 0.4em 0.8em;
  font-size: 0.85em;
}

.btn-group .btn {
  margin: 0 2px;
}

.modal.show {
  display: block;
}

code {
  background-color: #f8f9fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.9em;
}
</style>
